import { inject } from '@angular/core'
import { createEffect } from '@ngrx/effects'
import { Actions, ofType } from '@ngrx/effects'
import { ToastrService } from 'ngx-toastr'
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs'
import {
  createAssignedTo,
  createAssignedToFailure,
  createAssignedToSuccess,
  createPermissionSet,
  createPermissionSetFailure,
  createPermissionSetSuccess,
  deleteAssignedTo,
  deleteAssignedToFailure,
  deleteAssignedToSuccess,
  loadAssignedTo,
  loadAssignedToFailure,
  loadAssignedToSuccess,
  loadPermissionSetById,
  loadPermissionSetByIdFailure,
  loadPermissionSetByIdSuccess,
  loadPermissionSets,
  loadUnassignedUsers,
  loadUnassignedUsersFailure,
  loadUnassignedUsersSuccess,
  updatePermissionSet,
  updatePermissionSetFailure,
  updatePermissionSetSuccess,
  upsertValidUpto,
  upsertValidUptoSuccess
} from './permission-sets.actions'
import { loadPermissionSetsFailure, loadPermissionSetsSuccess } from './permission-sets.actions'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { PermissionSetService } from '../../services/permission-set.service'
// import { PermissionSetAssignedTo, AssignedToResponse } from './permission-sets.state'

export const loadPermissionSets$ = createEffect(
  (actions$ = inject(Actions), permissionSetService = inject(PermissionSetService)) =>
    actions$.pipe(
      ofType(loadPermissionSets),
      switchMap(action =>
        permissionSetService.getPermissionSets(action.params, action.page, action.limit).pipe(
          map(res => {
            return loadPermissionSetsSuccess({
              permissionSets: res.permissionSets,
              totalCount: res.totalCount
            })
          }),
          catchError(error => {
            // console.error('Load PermissionSets failed:', error.message) // Log errors here
            return of(loadPermissionSetsFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const createPermissionSet$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService), // Inject the ToastrService
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createPermissionSet),
      exhaustMap(({ name, permissions, businessId }) =>
        permissionSetService.createPermissionSet({ name, permissions, businessId }).pipe(
          map(created => {
            // Dispatch the success action
            const successAction = createPermissionSetSuccess({ permissionSet: created })

            // Show a success message using Toastr
            toastr.success('PermissionSet created successfully!', 'Success')
            // store.dispatch(loadPermissionSets({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return successAction
          }),
          catchError(error => {
            // Optionally, show an error message
            toastr.error(error.error.message, 'Error')
            return of(createPermissionSetFailure({ error }))
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const updatePermissionSet$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(updatePermissionSet),
      exhaustMap(({ id, name, permissions, businessId }) =>
        permissionSetService
          .updatePermissionSet({
            id,
            name,
            permissions,
            businessId
          })
          .pipe(
            map(updatedPermissionSet => {
              toastr.success('PermissionSet updated successfully!', 'Success')

              // Dispatch loadBusinessById after the update is successful
              // store.dispatch(loadPermissionSets({ params: null }))
              modalService.dismissAll()
              // Dispatch success action to update the store
              return updatePermissionSetSuccess({ permissionSet: updatedPermissionSet })
            }),
            catchError(error => {
              toastr.error('Failed to update PermissionSet', 'Error') // Show error message
              return of(updatePermissionSetFailure({ error }))
            })
          )
      )
    ),
  { functional: true }
)

export const loadPermissionSetById$ = createEffect(
  (actions$ = inject(Actions), permissionSetService = inject(PermissionSetService)) =>
    actions$.pipe(
      ofType(loadPermissionSetById),
      switchMap(({ id }) =>
        permissionSetService.getPermissionSetById(id).pipe(
          map(permissionSet => {
            // console.log('PermissionSet', PermissionSet)
            return loadPermissionSetByIdSuccess({ permissionSet })
          }),
          catchError(error => of(loadPermissionSetByIdFailure({ error: error.message })))
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const loadAssignedTo$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(loadAssignedTo),
      switchMap(action =>
        permissionSetService.getAssignedTo(action.permissionSetId).pipe(
          map(res =>
            loadAssignedToSuccess({
              totalCount: res.totalCount, // Adjusted if needed
              assignedPermissionSets: res.assignedPermissionSets
            })
          ),
          catchError(error => {
            toastr.error('Failed to fetch users: ' + error.message)
            return of(loadAssignedToFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const createAssignedTo$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(createAssignedTo),
      exhaustMap(({ permissionSetId, userData, businessId }) =>
        permissionSetService.createAssignedTo(permissionSetId, userData, businessId).pipe(
          map(d => {
            toastr.success('assigned successfully!', 'Success')
            return createAssignedToSuccess({ assignedTo: d })
          }),
          catchError(error => {
            toastr.error('Failed to Assign: ' + error.message)
            return of(createAssignedToFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const upsertValidUpto$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(upsertValidUpto),
      exhaustMap(({ id, validUpto }) =>
        permissionSetService.upsertValidUpto(id, validUpto).pipe(
          map(d => {
            const successAction = upsertValidUptoSuccess({ assignedTo: d })
            toastr.success('Updated successfully!', 'Success')
            return successAction
          }),
          catchError(error => {
            toastr.error(error.error.message, 'Error')
            return of(createPermissionSetFailure({ error }))
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const deleteAssignedTo$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(deleteAssignedTo),
      exhaustMap(action =>
        permissionSetService.deleteAssignedTo(action.id).pipe(
          map(d => deleteAssignedToSuccess({ assignedTo: d })),
          catchError(error => {
            toastr.error('Failed to delete assignment: ' + error.message)
            return of(deleteAssignedToFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const loadUnassignedUsers$ = createEffect(
  (
    actions$ = inject(Actions),
    permissionSetService = inject(PermissionSetService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(loadUnassignedUsers),
      switchMap(action =>
        permissionSetService
          .getUnssignedUsers(action.permissionSetId, action.businessId, action.businessIdtoAssign)
          .pipe(
            map(res =>
              loadUnassignedUsersSuccess({
                users: res.users,
                totalUserCount: res.totalCount
              })
            ),
            catchError(error => {
              toastr.error('Failed to fetch users: ' + error.message)
              return of(loadUnassignedUsersFailure({ error: error.message }))
            })
          )
      )
    ),
  { functional: true }
)

export const permissionSetEffects = {
  loadPermissionSets$,
  createPermissionSet$,
  updatePermissionSet$,
  createAssignedTo$,
  upsertValidUpto$,
  deleteAssignedTo$,
  loadPermissionSetById$,
  loadAssignedTo$,
  loadUnassignedUsers$
}
